// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MessageSquareText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CALL_CENTRE_PROFILE,
  API_CALL_TRANSCRIPTS_EXISTS,
  API_CALL_TRANSCRIPT_BY_TRACE,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import {
  getUserModuleOperations,
  hasCallCentreTranscriptionAcl,
  isCallCentreTranscriptionEnabled,
} from '@/utilities/callCentreTranscriptionAcl';
import { getAssignedCallCentreId } from '@/utilities/callCentreVoicemailAcl';

const LINES_PER_PAGE = 200;
/* Must not exceed the API's own per-request cap, or the tail is silently
 * dropped and those rows lose their button. */
const EXISTS_CHUNK = 200;

/* Availability answers never change for a finished call, so cache them for the
 * session — paging back and forth in a report re-asks nothing. */
const availabilityCache = new Map<string, number>();

function normalizeIds(ids: any[]) {
  const seen = new Set<string>();
  (Array.isArray(ids) ? ids : []).forEach((s) => {
    const id = String(s || '').trim();
    if (id) seen.add(id);
  });
  return [...seen];
}

const callCentreTranscriptionCache = new Map<string, Promise<boolean>>();

function fetchCallCentreTranscriptionEnabled(ccId: any) {
  const key = String(ccId);
  if (!callCentreTranscriptionCache.has(key)) {
    const p = getApiCall(API_CALL_CENTRE_PROFILE.replace(':id', key), {}, false)
      .then((resp: any) => isCallCentreTranscriptionEnabled(resp?.data || resp))
      .catch(() => {
        callCentreTranscriptionCache.delete(key);
        return false;
      });
    callCentreTranscriptionCache.set(key, p);
  }
  return callCentreTranscriptionCache.get(key)!;
}

export function useCanViewTranscripts() {
  const user = useMemo(() => getUser(), []);
  const role = user?.role;
  const isCallCentreScoped = role === USER_ROLES.CALL_CENTRE_ADMIN;

  const syncAnswer = useMemo(() => {
    if (!user) return false;
    if (role === USER_ROLES.SUPERADMIN || role === USER_ROLES.SUB_SUPERADMIN) return true;
    if (role === USER_ROLES.CALL_CENTRE_AGENT || role === USER_ROLES.CALL_CENTRE_TEAM_LEAD) {
      return !!(user?.agentData?.is_transcription_enabled ?? user?.agent_details?.is_transcription_enabled);
    }
    if (isCallCentreScoped) return null; // needs the call centre profile
    return hasCallCentreTranscriptionAcl(getUserModuleOperations(user), { userRole: role });
  }, [user, role, isCallCentreScoped]);

  const [ccEnabled, setCcEnabled] = useState(false);
  useEffect(() => {
    if (syncAnswer !== null) return undefined;
    const ccId = getAssignedCallCentreId(user);
    if (!ccId) return undefined;
    let cancelled = false;
    fetchCallCentreTranscriptionEnabled(ccId).then((ok) => {
      if (!cancelled) setCcEnabled(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [syncAnswer, user]);

  return syncAnswer === null ? ccEnabled : syncAnswer;
}

/**
 * Availability for one rendered page of rows.
 *
 * Returns a lookup `(traceId) => lineCount`. Unknown/absent = 0, which is what
 * the button treats as "no transcript". Users without transcript access always
 * get 0 (and nothing is fetched), so every button stays hidden.
 */
/* Fills availabilityCache for these trace ids. A client-side-paginated report can
 * hold far more rows than one request allows, so walk it in chunks — sequentially,
 * to keep a big CDR page from firing a burst of parallel requests at the API. */
export async function loadTranscriptAvailability(traceIds: any[], isCancelled = () => false, onChunk = () => {}) {
  const missing = normalizeIds(traceIds).filter((id) => !availabilityCache.has(id));
  for (let i = 0; i < missing.length && !isCancelled(); i += EXISTS_CHUNK) {
    const chunk = missing.slice(i, i + EXISTS_CHUNK);
    try {
      const data = await postApiCall(API_CALL_TRANSCRIPTS_EXISTS, {
        trace_ids: chunk,
      });
      const available = data?.available || {};
      // Cache the misses too — otherwise every re-render re-asks for rows
      // that will never have a transcript.
      chunk.forEach((id) => {
        availabilityCache.set(id, Number(available[id]) || 0);
      });
      if (!isCancelled()) onChunk();
    } catch {
      /* Availability is decoration — a failure just means no buttons. */
    }
  }
}

export function useTranscriptAvailability(traceIds: any[]) {
  const allowed = useCanViewTranscripts();
  const ids = useMemo(() => (allowed ? normalizeIds(traceIds) : []), [traceIds, allowed]);
  const key = useMemo(() => ids.join('|'), [ids]);
  const [, bump] = useState(0);

  useEffect(() => {
    const missing = ids.filter((id) => !availabilityCache.has(id));
    if (!missing.length) return undefined;

    let cancelled = false;
    loadTranscriptAvailability(missing, () => cancelled, () => bump((n) => n + 1));

    return () => {
      cancelled = true;
    };
  }, [key]);

  return useCallback((traceId: any) => {
    const id = String(traceId || '').trim();
    return allowed && id ? availabilityCache.get(id) || 0 : 0;
  }, [key, allowed]);
}

function formatLineTime(value: any) {
  if (!value) return '';
  // ClickHouse hands back "YYYY-MM-DD hh:mm:ss.SSS" (no zone); Date parses that
  // as local time on every browser we care about.
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString('en-IN', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function isCustomer(line: any) {
  return String(line?.speaker || '').toLowerCase() === 'customer';
}

function TranscriptLine({ line }: { line: any }) {
  const customer = isCustomer(line);
  return (
    <div className={'flex ' + (customer ? 'justify-end' : 'justify-start')}>
      <div className="max-w-[78%]">
        <div
          className={
            'mb-1 flex items-baseline gap-2 text-[11px] ' +
            (customer ? 'justify-end' : 'justify-start')
          }
        >
          <span
            className={
              'font-semibold uppercase tracking-wide ' +
              (customer
                ? 'text-slate-600 dark:text-slate-300'
                : 'text-sky-700 dark:text-sky-300')
            }
          >
            {customer ? 'Customer' : 'Agent'}
          </span>
          <span className="font-mono tabular-nums text-muted-foreground">
            {formatLineTime(line?.ts)}
          </span>
        </div>
        <div
          className={
            'rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ring-1 ring-inset ' +
            (customer
              ? 'bg-muted text-foreground ring-border'
              : 'bg-sky-50 text-sky-950 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-50 dark:ring-sky-800')
          }
        >
          {String(line?.text || '').trim() || '—'}
        </div>
      </div>
    </div>
  );
}

export function CallTranscriptPanel({ traceId }: { traceId: string }) {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState<any>(null);
  const [lines, setLines] = useState<any[]>([]);
  const [cursor, setCursor] = useState<any>(null);
  const cursorRef = useRef<any>(null);

  useEffect(() => {
    const trace = String(traceId || '').trim();
    if (!trace) {
      setError('This row has no Trace ID, so its transcript cannot be looked up.');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setLines([]);
    setSession(null);
    setCursor(null);
    cursorRef.current = null;

    getApiCall(`${API_CALL_TRANSCRIPT_BY_TRACE(trace)}?limit=${LINES_PER_PAGE}`)
      .then((data) => {
        if (cancelled) return;
        setSession(data?.session || null);
        setLines(Array.isArray(data?.lines) ? data.lines : []);
        setCursor(data?.next_cursor || null);
        cursorRef.current = data?.next_cursor || null;
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || String(err) || 'Failed to load transcript');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [traceId]);

  const loadMore = useCallback(() => {
    const next = cursorRef.current;
    const trace = String(traceId || '').trim();
    if (!next || !trace) return;
    cursorRef.current = null; // guard against a double-fire from fast scrolling
    setLoadingMore(true);
    const qs = new URLSearchParams({
      limit: String(LINES_PER_PAGE),
      after_ts: String(next.after_ts),
      after_sequence: String(next.after_sequence),
    });
    getApiCall(`${API_CALL_TRANSCRIPT_BY_TRACE(trace)}?${qs.toString()}`)
      .then((data) => {
        const more = Array.isArray(data?.lines) ? data.lines : [];
        setLines((prev) => [...prev, ...more]);
        setCursor(data?.next_cursor || null);
        cursorRef.current = data?.next_cursor || null;
      })
      .catch((err) => {
        setError(err?.message || String(err) || 'Failed to load more lines');
      })
      .finally(() => setLoadingMore(false));
  }, [traceId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading transcript…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200">
        {error}
      </div>
    );
  }

  if (!lines.length) {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
        No transcript recorded for this call.
        <div className="mt-2 font-mono text-xs text-foreground">{traceId || '—'}</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          Trace ID <span className="font-mono text-foreground">{traceId}</span>
        </span>
        <span>
          {lines.length}
          {cursor ? '+' : ''} line{lines.length === 1 ? '' : 's'}
          {session?.language ? ` · ${session.language}` : ''}
        </span>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-md bg-muted/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide">
        <span className="text-sky-700 dark:text-sky-300">Agent</span>
        <span className="text-slate-600 dark:text-slate-300">Customer</span>
      </div>

      <div className="space-y-2.5">
        {lines.map((line: any, index: number) => (
          <TranscriptLine key={`${line?.ts}-${line?.sequence}-${index}`} line={line} />
        ))}
      </div>

      {cursor ? (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
        >
          {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {loadingMore ? 'Loading…' : 'Load more lines'}
        </button>
      ) : null}
    </div>
  );
}

const TRANSCRIPT_DIALOG_Z = 'z-[1100]';

function stopOpenGesture(e: any) {
  e.preventDefault();
  e.stopPropagation();
}

export function CallTranscriptDialog({
  open,
  onOpenChange,
  traceId,
  preventOutsideClose = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  traceId: string;
  preventOutsideClose?: boolean;
}) {
  const blockOutside = (e: any) => {
    if (preventOutsideClose) e.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        overlayClassName={TRANSCRIPT_DIALOG_Z}
        className={
          TRANSCRIPT_DIALOG_Z +
          ' max-h-[88vh] w-[min(760px,calc(100vw-2rem))] max-w-3xl overflow-y-auto'
        }
        onPointerDownOutside={blockOutside}
        onInteractOutside={blockOutside}
        onFocusOutside={blockOutside}
      >
        <DialogHeader>
          <DialogTitle>Call transcript</DialogTitle>
          <DialogDescription>
            What was said on this call, in order. Agent on the left, customer on the right.
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open — nothing is fetched until someone asks. */}
        {open ? <CallTranscriptPanel traceId={traceId} /> : null}
      </DialogContent>
    </Dialog>
  );
}

export function CallTranscriptButton({
  traceId,
  lineCount = 0,
  className = '',
  onOpen,
}: {
  traceId?: string;
  lineCount?: number;
  className?: string;
  onOpen?: (traceId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const trace = String(traceId || '').trim();
  // No transcript for this call: render nothing at all rather than a dead button.
  if (!trace || !lineCount) return null;
  const lifted = typeof onOpen === 'function';

  const openTranscript = (e: any) => {
    // The report row itself is clickable — don't let the row handler fire too.
    stopOpenGesture(e);
    if (lifted) onOpen(trace);
    else setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onPointerDown={openTranscript}
        onClick={openTranscript}
        title={`View transcript (${lineCount} line${lineCount === 1 ? '' : 's'})`}
        aria-label="View call transcript"
        className={
          'inline-flex items-center justify-center gap-1 rounded-md bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 ' +
          className
        }
      >
        <MessageSquareText className="h-3.5 w-3.5" />
      </button>
      {lifted ? null : (
        <CallTranscriptDialog open={open} onOpenChange={setOpen} traceId={trace} />
      )}
    </>
  );
}
